import { PageContainer, PageHeader } from '@/components/Atoms'
import { Block } from '@/components/Block'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { Note } from '@/components/Note'
import { PendingChanges } from '@/components/PendingChanges'
import {
  ProductTypeMappingEdit,
  productTypeMappingsSearchSchema,
  ProductTypeMappingsSearch,
  useProductTypeMappings,
  useProductTypesList,
  useUpdateProductTypeMappings,
} from '@/hooks/queries/crt/useProductTypes'
import { useLastUpdatedProductTypeMappingsLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { UpdateProductTypeMappings } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangleIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ProductTypeMappingsDataTable from './-components/ProductTypeMappingsDataTable'

export const Route = createFileRoute('/_auth/_modules/crt/materials/insights/product-types/')({
  component: RouteComponent,
  validateSearch: (search) => productTypeMappingsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation('crt')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const [edited, setEdited] = useState<Map<string, ProductTypeMappingEdit>>(new Map())

  const {
    data: mappingsData,
    isPending: isMappingsPending,
    error: mappingsError,
  } = useProductTypeMappings()
  const { data: productTypesData, isPending: isProductTypesPending } = useProductTypesList()
  const { data: lastUpdatedTrail } = useLastUpdatedProductTypeMappingsLog()

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTrail)

  const { mutate: updateMappings, isPending: isSaving } = useUpdateProductTypeMappings()

  const rows = mappingsData?.results ?? []

  // Ignored is a decision, so only rows with neither a product type nor the flag are still open.
  const unmappedCount = rows.filter((row) => row.productTypeId === null && !row.isIgnored).length

  const showResults = (next: ProductTypeMappingsSearch) => navigate({ search: next })
  const resetAllFilters = () => navigate({ search: {} })

  const isSameAsOriginal = (original: ProductTypeMappingEdit, edit: ProductTypeMappingEdit) =>
    original.productTypeId === edit.productTypeId && original.isIgnored === edit.isIgnored

  const handleValueChange = (productTypeVariant: string, edit: ProductTypeMappingEdit) => {
    setEdited((prev) => {
      const next = new Map(prev)
      const original = rows.find((r) => r.productTypeVariant === productTypeVariant)
      const originalEdit: ProductTypeMappingEdit = {
        productTypeId: original?.productTypeId ?? null,
        isIgnored: original?.isIgnored ?? false,
      }
      if (isSameAsOriginal(originalEdit, edit)) {
        next.delete(productTypeVariant)
      } else {
        next.set(productTypeVariant, edit)
      }
      return next
    })
  }

  const handleBulkEdit = (productTypeVariants: string[], edit: ProductTypeMappingEdit) => {
    const updates: UpdateProductTypeMappings['updates'] = []
    for (const productTypeVariant of productTypeVariants) {
      const original = rows.find((r) => r.productTypeVariant === productTypeVariant)
      const current = edited.get(productTypeVariant) ?? {
        productTypeId: original?.productTypeId ?? null,
        isIgnored: original?.isIgnored ?? false,
      }
      if (!isSameAsOriginal(current, edit)) {
        updates.push({ productTypeVariant, ...edit })
      }
    }

    if (updates.length === 0) return

    updateMappings(
      { updates },
      {
        onSuccess: () => {
          setEdited((prev) => {
            if (prev.size === 0) return prev
            const next = new Map(prev)
            for (const variant of productTypeVariants) {
              next.delete(variant)
            }
            return next.size === prev.size ? prev : next
          })
        },
      },
    )
  }

  const handleSaveChanges = () => {
    if (edited.size === 0) return

    const updates: UpdateProductTypeMappings['updates'] = []
    edited.forEach((edit, productTypeVariant) => {
      updates.push({ productTypeVariant, ...edit })
    })

    updateMappings(
      { updates },
      {
        onSuccess: () => {
          setEdited(new Map())
        },
      },
    )
  }

  const areChangesPending = edited.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <PageHeader>{t('productTypes.pageHeader')}</PageHeader>

      {unmappedCount > 0 && (
        <Note
          tone="warning"
          icon={AlertTriangleIcon}
          note={t('productTypes.unmapped.banner', { count: unmappedCount })}
        />
      )}

      <ProductTypeMappingsDataTable
        data={rows}
        productTypes={productTypesData?.results ?? []}
        isPending={isMappingsPending || isProductTypesPending}
        error={mappingsError}
        edited={edited}
        search={search}
        onValueChange={handleValueChange}
        onBulkEdit={handleBulkEdit}
        onShowResults={showResults}
        onResetAllFilters={resetAllFilters}
      />

      <PermissionsGuard required={['crt_product_types_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          lastUpdatedLog={lastUpdatedLog}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
